// History-API router (no hash): path patterns with `:param` segments, resolution against the
// session (sign-in / forbidden / not found, FLOW-002), and the permission-filtered navigation.
// The browser objects are injected so the whole module runs without a DOM.
import { createContext } from "preact";
import type { Session } from "../session/session";
import { NAV_GROUPS, NAV_LABELS, NAV_ORDER, type NavId, type RouteParams, type ScreenRoute } from "./route";

export type Resolution =
  | { kind: "screen"; route: ScreenRoute; params: Record<string, string>; query: URLSearchParams }
  | { kind: "forbidden"; route: ScreenRoute }
  | { kind: "sign-in"; returnTo: string }
  | { kind: "not-found" };

export interface RouterLocation {
  pathname: string;
  search: string;
}

function segments(path: string): string[] {
  return path.split("/").filter((segment) => segment !== "");
}

/** The parameters of `pathname` under `pattern`, or null when it does not match. */
export function matchPath(pattern: string, pathname: string): Record<string, string> | null {
  const want = segments(pattern);
  const have = segments(pathname);
  if (want.length !== have.length) return null;
  const params: Record<string, string> = {};
  for (const [index, part] of want.entries()) {
    const actual = have[index] as string;
    if (part.startsWith(":")) {
      try {
        params[part.slice(1)] = decodeURIComponent(actual);
      } catch {
        return null;
      }
    } else if (part !== actual) {
      return null;
    }
  }
  return params;
}

/** Fill a pattern's parameters, each URI-encoded. */
export function buildPath<P extends string>(pattern: P, params: RouteParams<P>): string {
  const values = params as Record<string, string>;
  const path = segments(pattern)
    .map((part) => (part.startsWith(":") ? encodeURIComponent(values[part.slice(1)] ?? "") : part))
    .join("/");
  return `/${path}`;
}

// A literal segment beats a parameter, so /installer/accounts/:orgId wins over /installer/:x/:y.
function specificity(route: ScreenRoute): number[] {
  return segments(route.path).map((part) => (part.startsWith(":") ? 0 : 1));
}

function compareSpecificity(a: ScreenRoute, b: ScreenRoute): number {
  const sa = specificity(a);
  const sb = specificity(b);
  for (let i = 0; i < Math.min(sa.length, sb.length); i++) {
    const diff = (sb[i] as number) - (sa[i] as number);
    if (diff !== 0) return diff;
  }
  // Routes of different lengths never match the same path; ordering them keeps the comparison a
  // total order, so the sort cannot depend on where the routes sit in the table.
  return sb.length - sa.length;
}

export function resolve(routes: readonly ScreenRoute[], location: RouterLocation, session: Session | null): Resolution {
  const ordered = [...routes].sort(compareSpecificity);
  for (const route of ordered) {
    const params = matchPath(route.path, location.pathname);
    if (params === null) continue;
    if (route.permission !== "public") {
      if (session === null) return { kind: "sign-in", returnTo: location.pathname + location.search };
      if (!session.allows(route.permission)) return { kind: "forbidden", route };
    }
    return { kind: "screen", route, params, query: new URLSearchParams(location.search) };
  }
  return { kind: "not-found" };
}

export interface NavEntry {
  nav: NavId;
  label: string;
  href: string;
}

/** One entry per NAV node the principal may open: its first parameter-free route, in sidebar order. */
export function navEntries(routes: readonly ScreenRoute[], session: Session): NavEntry[] {
  const entries: NavEntry[] = [];
  for (const nav of NAV_ORDER) {
    const route = routes.find(
      (r) => r.nav === nav && !r.path.includes(":") && r.permission !== "public" && session.allows(r.permission),
    );
    if (route) entries.push({ nav, label: NAV_LABELS[nav], href: route.path });
  }
  return entries;
}

export interface NavSection {
  label: string | null;
  entries: NavEntry[];
}

/** The permitted entries under their sidebar headings; a heading with nothing permitted is dropped. */
export function navSections(entries: readonly NavEntry[]): NavSection[] {
  return NAV_GROUPS.map((group) => ({
    label: group.label,
    entries: entries.filter((entry) => group.items.includes(entry.nav)),
  })).filter((section) => section.entries.length > 0);
}

/** Whether a click on a same-origin link should become a client-side navigation. */
export function isPlainLeftClick(event: {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  defaultPrevented: boolean;
}): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && !event.defaultPrevented;
}

export interface BrowserHistory {
  location: RouterLocation;
  history: { pushState(data: unknown, unused: string, url: string): void; replaceState(data: unknown, unused: string, url: string): void };
  addEventListener(type: "popstate", listener: () => void): void;
  removeEventListener(type: "popstate", listener: () => void): void;
}

export interface Router {
  location(): RouterLocation;
  navigate(to: string, options?: { replace?: boolean }): void;
  subscribe(listener: (location: RouterLocation) => void): () => void;
  dispose(): void;
}

export function createRouter(browser: BrowserHistory): Router {
  const listeners = new Set<(location: RouterLocation) => void>();
  const snapshot = (): RouterLocation => ({ pathname: browser.location.pathname, search: browser.location.search });
  const notify = (): void => {
    const location = snapshot();
    for (const listener of [...listeners]) listener(location);
  };
  browser.addEventListener("popstate", notify);
  return {
    location: snapshot,
    navigate(to, options = {}) {
      if (!to.startsWith("/") || to.startsWith("//")) throw new Error("navigate takes a same-origin path");
      if (options.replace) browser.history.replaceState(null, "", to);
      else browser.history.pushState(null, "", to);
      notify();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    dispose() {
      browser.removeEventListener("popstate", notify);
      listeners.clear();
    },
  };
}

/** The app's router, for links and redirects inside screens (provided by main.tsx). */
export const RouterContext = createContext<Router | null>(null);
